import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input, FormField } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import api from '../../services/api';

export default function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { t } = useTranslation();

  const [invite, setInvite]             = useState(null);
  const [tokenError, setTokenError]     = useState('');
  const [tokenLoading, setTokenLoading] = useState(true);

  const [fullname, setFullname]         = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError]       = useState('');
  const [fieldErrors, setFieldErrors]   = useState({});
  const [submitting, setSubmitting]     = useState(false);
  const [done, setDone]                 = useState(false);

  const roleLabel = (role) => t(`roles.${role}`, { defaultValue: role });

  useEffect(() => {
    if (!token) {
      setTokenError(t('signup.invalid_link_default'));
      setTokenLoading(false);
      return;
    }
    api.get(`/users/verify-invite?token=${encodeURIComponent(token)}`)
      .then((r) => setInvite(r.data))
      .catch((e) => {
        const msg =
          e?.response?.data?.message ||
          t('signup.invalid_link_expired');
        setTokenError(typeof msg === 'string' ? msg : t('signup.invalid_link_default'));
      })
      .finally(() => setTokenLoading(false));
  }, [token, t]);

  function validate() {
    const errs = {};
    if (!fullname.trim() || fullname.trim().length < 2) {
      errs.fullname = t('signup.fullname_min');
    }
    if (password.length < 8) {
      errs.password = t('signup.password_min');
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      await api.post('/users/complete-signup', {
        token,
        fullname: fullname.trim(),
        password,
      });
      setDone(true);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t('common.error_occurred');
      setFormError(typeof msg === 'string' ? msg : t('common.error_occurred'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary flex-col justify-between p-10 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-100px] left-[-100px] w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-[-80px] right-[-80px] w-80 h-80 rounded-full bg-accent/20 blur-3xl" />
        </div>
        <div className="relative flex items-center gap-3">
          <div className="w-16 h-15 rounded-lg bg-white flex items-center justify-center overflow-hidden">
            <img
              src="/mfb-logo.png"
              alt="MFB"
              className="w-20 h-12 object-contain mb-1"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement.innerHTML =
                  '<span class="text-accent font-black text-sm">MFB</span>';
              }}
            />
          </div>
          <div>
            <p className="text-white font-bold text-[15px] leading-tight">{t('brand.name')}</p>
            <p className="text-white/50 text-[11px]">{t('brand.tagline')}</p>
          </div>
        </div>
        <div className="relative">
          <h1 className="text-4xl font-bold leading-tight text-white mb-4">
            {t('signup.headline')}{' '}
            <span className="text-accent">{t('signup.headline_accent')}</span>
          </h1>
          <p className="text-white/60 text-[13px] leading-relaxed max-w-sm">
            {t('signup.subheadline')}
          </p>
        </div>
        <p className="relative text-white/30 text-[11px]">{t('brand.footer')}</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col bg-white">
        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-sm">

            {/* Loading */}
            {tokenLoading && (
              <div className="text-center text-slate-500 text-[13px] py-10">
                {t('signup.verifying')}
              </div>
            )}

            {/* Token error */}
            {!tokenLoading && tokenError && (
              <div className="flex flex-col items-center gap-4 py-10 text-center">
                <XCircle size={40} strokeWidth={1.25} className="text-rose-400" />
                <p className="text-slate-700 font-semibold text-[15px]">{t('signup.invalid_link')}</p>
                <p className="text-[13px] text-slate-500">{tokenError}</p>
                <Link to="/login" className="text-[13px] text-primary hover:underline">
                  {t('signup.back_to_login')}
                </Link>
              </div>
            )}

            {/* Success */}
            {done && (
              <div className="flex flex-col items-center gap-4 py-10 text-center">
                <CheckCircle2 size={40} strokeWidth={1.25} className="text-emerald-500" />
                <p className="text-slate-700 font-semibold text-[15px]">{t('signup.success_title')}</p>
                <p className="text-[13px] text-slate-500">{t('signup.success_message')}</p>
                <Button
                  variant="primary"
                  size="md"
                  iconRight={ArrowRight}
                  onClick={() => navigate('/login')}
                >
                  {t('login.submit')}
                </Button>
              </div>
            )}

            {/* Sign-up form */}
            {!tokenLoading && !tokenError && !done && invite && (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-900">{t('signup.form_title')}</h2>
                  <p className="text-[13px] text-slate-500 mt-1">{t('signup.form_subtitle')}</p>
                </div>

                {/* Locked invite info */}
                <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{t('signup.email_label')}</span>
                    <span className="text-[13px] text-slate-700 font-medium">{invite.email}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{t('signup.role_label')}</span>
                    <span className="text-[13px] text-slate-700 font-medium">
                      {roleLabel(invite.role)}
                    </span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <FormField
                    label={t('signup.fullname_label')}
                    required
                    helper={fieldErrors.fullname}
                    error={!!fieldErrors.fullname}
                  >
                    <Input
                      type="text"
                      placeholder={t('signup.fullname_placeholder')}
                      value={fullname}
                      onChange={(e) => {
                        setFullname(e.target.value);
                        if (fieldErrors.fullname) setFieldErrors((p) => ({ ...p, fullname: undefined }));
                      }}
                      autoComplete="name"
                      state={fieldErrors.fullname ? 'error' : 'default'}
                      required
                    />
                  </FormField>

                  <FormField
                    label={t('signup.password_label')}
                    required
                    helper={fieldErrors.password || t('signup.password_min')}
                    error={!!fieldErrors.password}
                  >
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (fieldErrors.password) setFieldErrors((p) => ({ ...p, password: undefined }));
                        }}
                        autoComplete="new-password"
                        state={fieldErrors.password ? 'error' : 'default'}
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
                        tabIndex={-1}
                      >
                        {showPassword
                          ? <EyeOff size={14} strokeWidth={1.75} />
                          : <Eye size={14} strokeWidth={1.75} />}
                      </button>
                    </div>
                  </FormField>

                  {formError && (
                    <p className="text-[12px] text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                      {formError}
                    </p>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    loading={submitting}
                    iconRight={!submitting ? ArrowRight : undefined}
                    className="w-full mt-2"
                  >
                    {t('signup.submit')}
                  </Button>
                </form>

                <p className="text-center text-[11px] text-slate-400 mt-6">
                  {t('signup.already_registered')}{' '}
                  <Link to="/login" className="text-primary hover:underline">{t('signup.signin_link')}</Link>
                </p>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
